// pages/MapView.js
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import DataGrid, {
  Column,
  Paging,
  FilterRow,
  Sorting,
} from 'devextreme-react/data-grid';
import DateBox from 'devextreme-react/date-box';
import SelectBox from 'devextreme-react/select-box';
import { Button } from 'devextreme-react/button';
import 'devextreme/dist/css/dx.material.blue.light.css';
import WaferMapCanvas from "../components/WaferMapCanvas";
import API_URL from "../config";

export default function MapView({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [allWafersData, setAllWafersData] = useState([]);
  const [uniqueLots, setUniqueLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState(null);
  const [mapInfo, setMapInfo] = useState({ data: [], yld: null });
  const [loading, setLoading] = useState({ grid: false, device: false, map: false });
  const [error, setError] = useState("");

  const formatDate = (date) => {
    if (!date) return "";
    const y = date.getFullYear().toString();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}${m}${d}000000`;
  };

  // 고객 목록 조회
  useEffect(() => {
    (async () => {
      setLoading(prev => ({ ...prev, device: true }));
      setError("");
      try {
        const res = await axios.get(`${API_URL}/api/oracle/PT_cust`);
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        const list = res.data.data || [];
        setCustList(list);
        if (user?.role === "CUST") {
          const me = list.find(i => i.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
          setSelectedCust(me?.CODE_CD || null);
          if (!me) setError(`사용자 코드(${user.username})가 목록에 없습니다.`);
        }
      } catch (e) {
        setError(`CUST 로드 오류: ${e.message}`);
      } finally {
        setLoading(prev => ({ ...prev, device: false }));
      }
    })();
  }, [user]);

  // DEVICE 목록 조회
  useEffect(() => {
    setSelectedDevice(null);
    if (!selectedCust) {
      setDeviceList([]);
      return;
    }
    (async () => {
      setLoading(prev => ({ ...prev, device: true }));
      setError("");
      try {
        const res = await axios.get(`${API_URL}/api/oracle/PT_device`, {
          params: { custCd: selectedCust }
        });
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        const list = res.data.data.map(r => ({ CODE_CD: r.CODE_CD, NAME: r.CODE_CD })) || [];
        setDeviceList(list);
      } catch (e) {
        setError(`DEVICE 로드 오류: ${e.message}`);
      } finally {
        setLoading(prev => ({ ...prev, device: false }));
      }
    })();
  }, [selectedCust]);

  // 데이터 조회 및 TESTAREA 포함 LOT 고유화
  const fetchData = async () => {
    if (!selectedCust || !selectedDevice) {
      setError("Please select CUST and DEVICE first.");
      return;
    }
    setLoading(prev => ({ ...prev, grid: true }));
    setError("");
    setAllWafersData([]);
    setUniqueLots([]);
    setSelectedLot(null);
    setMapInfo({ data: [], yld: null });

    try {
      const params = {
        inDateFrom: formatDate(dateFrom),
        inDateTo: formatDate(dateTo),
        inCustCd: selectedCust,
        inDevice: selectedDevice,
      };
      const res = await axios.get(`${API_URL}/api/oracle/mapView`, { params });
      if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
      const allWafers = res.data.data.map((row, idx) => ({ id: `${row.WAFER_SEQ}-${idx}`, ...row })) || [];
      setAllWafersData(allWafers);

      // LOT_ID + TESTAREA 조합으로 고유 LOT 추출
      const lotMap = new Map();
      allWafers.forEach(item => {
        const key = `${item.LOT_ID}_${item.TESTAREA || ''}`;
        if (!lotMap.has(key)) {
          lotMap.set(key, {
            LOT_ID: item.LOT_ID,
            DEVICE: item.DEVICE,
            TESTAREA: item.TESTAREA || '',
          });
        }
      });
      setUniqueLots(Array.from(lotMap.values()));
    } catch (e) {
      setError(`조회 오류: ${e.message}`);
    } finally {
      setLoading(prev => ({ ...prev, grid: false }));
    }
  };

  // Map 데이터 조회
  const fetchMapData = async (waferSeq, mapTable) => {
    setLoading(prev => ({ ...prev, map: true }));
    setMapInfo({ data: [], yld: null });
    setError("");
    try {
      const res = await axios.get(`${API_URL}/api/oracle/waferMap`, {
        params: { waferSeq, mapTable }
      });
      if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
      const mapData = res.data.data;
      let calculatedYld = null;
      if (mapData && mapData.length > 0) {
        const totalDies = mapData.length;
        const bin1Count = mapData.filter(d => d.SBIN === 1).length;
        calculatedYld = (bin1Count / totalDies) * 100;
      }
      setMapInfo({ data: mapData, yld: calculatedYld });
    } catch (e) {
      setError(`Map Retrieval Error: ${e.message}`);
    } finally {
      setLoading(prev => ({ ...prev, map: false }));
    }
  };

  const handleLotSelectionChanged = (e) => {
    if (e.row && e.row.data) {
      setSelectedLot(e.row.data);
    }
  };

  // LOT 선택 시 LOT_ID + TESTAREA 기준 Wafer 필터링
  const wafersForSelectedLot = useMemo(() => {
    if (!selectedLot) return [];
    return allWafersData.filter(w =>
      w.LOT_ID === selectedLot.LOT_ID &&
      (w.TESTAREA || '') === (selectedLot.TESTAREA || '')
    );
  }, [selectedLot, allWafersData]);

  return (
    <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 40px)', padding: 0, boxSizing: 'border-box', overflow: 'hidden' }}>
      <div style={{ flex: '0 0 750px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {loading.grid && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading Data...</div>
          </div>
        )}
        <h4>Wafer Selection</h4>
        <div style={{ marginBottom: 10, padding: 10, border: "1px solid #eee", borderRadius: "4px" }}>
          <div style={{ display: "flex", flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <DateBox value={dateFrom} onValueChanged={e => setDateFrom(e.value)} label="From" width={200} />
            <DateBox value={dateTo} onValueChanged={e => setDateTo(e.value)} label="To" width={200} />
            <SelectBox dataSource={custList} value={selectedCust} onValueChanged={e => setSelectedCust(e.value)} valueExpr="CODE_CD" displayExpr="CODE_CD" placeholder="CUST" disabled={user?.role === "CUST"} searchEnabled width={200} />
            <SelectBox dataSource={deviceList} value={selectedDevice} onValueChanged={e => setSelectedDevice(e.value)} valueExpr="CODE_CD" displayExpr="NAME" placeholder="DEVICE" disabled={!selectedCust || loading.device} searchEnabled width={250} />
            <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading.grid} />
          </div>
        </div>

        {error && <p style={{ color: "red", margin: "0 0 10px 0" }}>{error}</p>}

        <div style={{ flex: 1, display: 'flex', gap: '10px', minHeight: 0 }}>
          <div style={{ flex: 1, height: '500px', display: 'flex', flexDirection: 'column' }}>
            <h5>Lots</h5>
            <DataGrid
              dataSource={uniqueLots}
              keyExpr={item => `${item.LOT_ID}_${item.TESTAREA}`} // keyExpr 문자열만 받기 때문에 임시 처리 필요, 혹은 uniqueLots에 key 필드 추가 권장
              height="100%"
              showBorders
              columnAutoWidth
              hoverStateEnabled
              focusedRowEnabled
              onFocusedRowChanged={handleLotSelectionChanged}
            >
              <Paging defaultPageSize={100} />
              <FilterRow visible={true} />
              <Sorting mode="multiple" />
              <Column dataField="DEVICE" width={150} />
              <Column dataField="TESTAREA" caption="TESTAREA" />
              <Column dataField="LOT_ID" />
            </DataGrid>
          </div>

          <div style={{ flex: 1, height: '500px', display: 'flex', flexDirection: 'column' }}>
            <h5>Wafers in Lot: {selectedLot ? `${selectedLot.LOT_ID} / ${selectedLot.TESTAREA || ''}` : 'N/A'}</h5>
            <DataGrid
              dataSource={wafersForSelectedLot}
              keyExpr="id"
              height="100%"
              showBorders
              columnAutoWidth
              hoverStateEnabled
              onRowDblClick={(e) => {
                const { WAFER_SEQ, MAP_TABLE_NAME } = e.data;
                fetchMapData(WAFER_SEQ, MAP_TABLE_NAME);
              }}
            >
              <Paging defaultPageSize={100} />
              <Column dataField="WAFER_NO" caption="Wafer No" />
              <Column dataField="WAFER_SEQ" caption="Wafer Seq" visible={false} />
              <Column dataField="TOTAL_DIE" caption="TOTAL DIE" visible={false}  />     
            </DataGrid>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0, border: "1px solid #ccc", padding: 10, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <h4>Wafer Map Result</h4>
        {loading.map && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading Map...</div>
          </div>
        )}
        <div style={{ width: '100%', height: '700px', overflowX: 'auto', overflowY: 'auto', flex: '0 0 auto', border: '1px solid #eee' }}>
          {mapInfo.data.length === 0 && !loading.map ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#888' }}>
              <p>Double-click a wafer in the list to display the map.</p>
            </div>
          ) : (
            <WaferMapCanvas mapInfo={mapInfo} />
          )}
        </div>
      </div>
    </div>
  );
}
